<template>
  <div class="so-orders-do-cliente-table">
    <div class="tab-content">
      <data-table
        :config="tableConfig"
        :data="tableData"
        :filterConfig="filterConfig"
        :refreshButton="refreshButton"
        @edit-click="handleEditClick"
        @refresh="loadData">
      </data-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import { useSOOrders } from '../../composables/useSOOrders';
import { useSOStatusMap } from '../../composables/useSOStatusMap';
import type { Orders_PorClienteRow } from '../../contracts/Orders_PorClienteRow';
import '../../contracts/Orders_PorClienteResponse';

type Id = number | string;

// Backend row shape comes from the contract. UI-only extras (`ID` for grid
// row identity, `_isFrozenFooter` for footer rendering) are layered on top.
interface SOOrderRow extends Partial<Orders_PorClienteRow> {
  ID?: Id;
  _isFrozenFooter?: boolean;
}

interface CellParams {
  value: unknown;
  data: SOOrderRow;
}

// Props
const props = defineProps<{
  clienteId: Id;
}>();

// Composables
const { t } = useI18n();
const { fetchServiceOrdersByCliente } = useSOOrders();
const { getOrderStatus, getFinancialStatus } = useSOStatusMap();

// Reactive data
const tableData = ref<SOOrderRow[]>([]);

// Computed
const tableConfig = computed(() => {
  return {
    tableId: 'tabelaSOOrdersCliente',
    pageLength: 100,
    showFilters: false,
    sortable: false,
    columns: tableColumns.value,
    pagination: false,
    stripedRows: true,
    showGridlines: true,
    resizableColumns: true,
    size: 'small',
    heightMode: 'fill-parent'
  };
});

const filterConfig = computed(() => {
  return {
    columns: {}
  };
});

const refreshButton = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const tableColumns = computed(() => {
  return [
    {
      data: 'so_order_id',
      title: t('ID'),
      renderType: 'clickable_id',
      width: 100
    },
    {
      data: 'so_order_status',
      title: t('Status'),
      width: 200,
      cellRenderer: (params: CellParams): string => {
        // For frozen footer row, show raw value
        if (params.data && params.data._isFrozenFooter) {
          return params.value != null ? String(params.value) : '';
        }

        const { label, severity, icon } = getOrderStatus(params.value as string);
        return `<span class='p-tag p-tag-${severity}'><i class='pi ${icon} p-tag-icon'></i> <span class="p-tag-value">${label}</span></span>`;
      }
    },
    {
      data: 'so_order_total',
      title: t('Total'),
      width: 150,
      renderType: 'currency',
      footerType: 'sum'
    },
    {
      data: 'so_order_created_at',
      title: t('Criado em'),
      width: 180,
      renderType: 'datetime'
    },
    {
      data: 'so_order_items_count',
      title: t('Itens'),
      width: 100
    },
    {
      data: 'so_order_financial_status',
      title: t('Status Financeiro'),
      width: 150,
      cellRenderer: (params: CellParams): string => {
        // For frozen footer row, show raw value
        if (params.data && params.data._isFrozenFooter) {
          return params.value != null ? String(params.value) : '';
        }

        const { label, severity } = getFinancialStatus(params.value as string);
        return `<span class='p-tag p-tag-${severity}'><span class="p-tag-value">${label}</span></span>`;
      }
    }
  ];
});

// Methods
// Clicking the order id opens the service-order modal (PDVCheckoutModal, centralized in
// ModalManager) — the same `modal.pdv-checkout.open` { orderId } event SOOrdersTable and
// PDVEntregasPage use.
const handleEditClick = (soOrderId: Id): void => {
  log('SOOrdersDoClienteTable', 'handleEditClick', 'Opening service order', soOrderId);
  eventBus.emit('modal.pdv-checkout.open', { orderId: soOrderId });
};

const loadData = async (): Promise<void> => {
  log('SOOrdersDoClienteTable', 'loadData', 'Loading SO orders for client', props.clienteId);

  if (!props.clienteId || props.clienteId === 0) {
    tableData.value = [];
    return;
  }

  try {
    const orders = await fetchServiceOrdersByCliente(Number(props.clienteId));
    tableData.value = orders || [];
    log('SOOrdersDoClienteTable', 'loadData', 'Loaded records', tableData.value.length);
  } catch (error) {
    log('SOOrdersDoClienteTable', 'loadData', 'Error loading SO orders', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar ordens de serviço: ') + message);
    tableData.value = [];
  }
};

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
.so-orders-do-cliente-table {
  padding: 15px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.tab-content {
  flex: 1;
  min-height: 300px;
  display: flex;
  flex-direction: column;
}
</style>
